import { Options as OriginalOptions } from '../sequenceserver/public/js/options.js';

export class Options extends OriginalOptions {
    constructor(props) {
        super(props);

        this.state = {
            textValue: '',
            objectValue: this.defaultObjectValue(),
            paramsMode: 'simple'
        };

        this.onIndividualOptionChanged = this.onIndividualOptionChanged.bind(this);
        this.simpleParamsJSX = this.simpleParamsJSX.bind(this);
        this.jobNameJSX = this.jobNameJSX.bind(this);
    }

    onIndividualOptionChanged(optionName, value) {
        let newObjectValue = { ...this.state.objectValue };
        newObjectValue[optionName] = value;

        let newTextValue = Object.entries(newObjectValue).map(([key, value]) => {
            if (value) {
                return `-${key} ${value}`;
            }
        }).reverse().join(' ').trim();

        this.setState({
            textValue: newTextValue,
            objectValue: newObjectValue
        });
    }

    optionsPresetsJSX() {
        return (
            <div id="options-presets" className="w-full">
                <h3 className="md:text-lg w-full font-medium border-b border-seqorange mb-2">Settings</h3>

                { Object.keys(this.props.predefinedOptions).length <= 1 &&
                    <p>Once query sequence and database type are selected, choose predefined BLAST parameters.</p>}

                { Object.keys(this.props.predefinedOptions).length > 1 && <>
                    <p className="text-gray-700">Choose a predefined setting or customize BLAST parameters.</p>
                    {this.presetListJSX()}
                </>}
            </div>
        );
    }

    handleParamsModeClick(mode) {
        this.setState({ paramsMode: mode });
    }

    taskSimpleJSX(inputClassNames) {
        let taskOptions = this.props.blastTasks || [];

        return (
            <div className="flex flex-col">
                <label htmlFor="task" className="md:text-lg">Task:</label>
                <select
                    id="task"
                    name="task"
                    disabled={taskOptions.length === 0}
                    className={inputClassNames}
                    value={this.state.objectValue.task}
                    onChange={e => this.onIndividualOptionChanged('task', e.target.value)}>
                        <option value="">{taskOptions.length === 0 ? "N/A" : "Select..."}</option>
                    {taskOptions.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                    ))}
                </select>
            </div>
        )
    }

    evaluesSimpleJSX(inputClassNames) {
        let evalueOptions = ["1e-100", "1e-50", "1e-20", "1e-10", "1e-5", "0.001", "1", "10"];
        // If this.state.objectValue.evalue is set from advanced params, include it in the evalueOptions
        if (this.state.objectValue.evalue && !evalueOptions.includes(this.state.objectValue.evalue.toString())) {
            evalueOptions.push(this.state.objectValue.evalue.toString());
        }
        // Ensure evalueOptions array is unique
        evalueOptions = [...new Set(evalueOptions)];

        return (
            <div className="flex flex-col">
                <label htmlFor="evalue" className="md:text-lg">E-value cutoff:</label>
                <select
                    id="evalue"
                    name="evalue"
                    className={inputClassNames}
                    value={this.state.objectValue.evalue}
                    onChange={e => this.onIndividualOptionChanged('evalue', e.target.value)}
                    placeholder="e.g. 1.0e-5">
                        <option value="">Select...</option>
                    {evalueOptions.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                    ))}
                </select>
            </div>
        )
    }

    maxTargetSeqsSimpleJSX(inputClassNames) {
        let maxTargetSeqsOptions = ["1", "3", "5", "10", "20", "50", "100", "500"];
        // If this.state.objectValue.max_target_seqs is set from advanced params, include it in the maxTargetSeqsOptions
        if (this.state.objectValue.max_target_seqs && !maxTargetSeqsOptions.includes(this.state.objectValue.max_target_seqs.toString())) {
            maxTargetSeqsOptions.push(this.state.objectValue.max_target_seqs.toString());
        }
        // Ensure maxTargetSeqsOptions array is unique
        maxTargetSeqsOptions = [...new Set(maxTargetSeqsOptions)];

        return (
            <div className="flex flex-col">
                <label htmlFor="max_target_seqs" className="md:text-lg">Maximum number of hits:</label>
                <select
                    id="max_target_seqs"
                    name="max_target_seqs"
                    className={inputClassNames}
                    value={this.state.objectValue.max_target_seqs}
                    onChange={e => this.onIndividualOptionChanged('max_target_seqs', e.target.value)}
                    placeholder="e.g. 50">
                    <option value="">Select...</option>
                    {maxTargetSeqsOptions.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                    ))}
                </select>
            </div>
        )
    }

    simpleParamsJSX() {
        if (this.state.paramsMode !== 'simple') {
            return null;
        }

        let inputClassNames = 'flex-grow block px-4 py-1 text-gray-900 border border-gray-300 rounded-lg bg-gray-50 text-base font-mono';

        return (
            <div className="w-full">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {this.taskSimpleJSX(inputClassNames)}
                    {this.evaluesSimpleJSX(inputClassNames)}
                    {this.maxTargetSeqsSimpleJSX(inputClassNames)}
                </div>
            </div>
        );
    }

    jobNameJSX() {
        return (
            <div className="w-full">
                <div className="flex items-end">
                    <label className="flex items-center md:text-lg" htmlFor="advanced">
                        Name:
                    </label>
                </div>

                <div className="flex-grow flex w-full">
                    <input type="text" className="flex-grow block px-4 py-1 text-gray-900 border border-gray-300 rounded-lg bg-gray-50 text-base font-mono"
                        name="name"
                        placeholder="e.g. GLP-1 against Ant Proteomes"
                        title="Descriptive name for the search"
                        maxLength="256"
                        pattern="[\p{L}\p{N}\-_:,.\(\)? ]*"
                        onInvalid={(e) => e.target.setCustomValidity("Please use up to 256 letters, numbers, spaces and -_:,.()?")}
                        onInput={(e) => e.target.setCustomValidity("")}
                    />
                </div>
                <div className="mt-1 text-gray-700">
                    An optional, descriptive name for the search to help it find later.
                </div>
            </div>
        )
    }

    render() {
        let activeTabClassNames = ' border-seqorange font-medium -mb-px bg-white';

        return (
            <div className="flex-grow flex flex-col items-start sm:items-center space-y-4">
                {this.optionsPresetsJSX()}

                <div className="w-full border-b border-seqorange mb-2 flex space-x-2">
                    <button
                        className={`px-4 py-1 border-t border-x rounded-t ${this.state.paramsMode === 'simple'? activeTabClassNames : ''}`}
                        type="button"
                        onClick={ () => { this.handleParamsModeClick('simple')} }
                    >
                        Customize
                    </button>
                    <button
                        className={`px-4 py-1 border-t border-x rounded-t ${this.state.paramsMode === 'advanced'? activeTabClassNames : ''}`}
                        type="button"
                        onClick={ () => { this.handleParamsModeClick('advanced')} }
                    >
                        Advanced
                    </button>
                </div>

                {this.simpleParamsJSX()}

                {this.advancedParamsJSX()}

                {this.jobNameJSX()}
            </div>
        );
    }
}
